/**
 * Renders paginated Status logs grid using payload-shaped API rows and {@link ConfiguredDataTable}.
 * Normalizes backend `ts` to `createdAt` so configs, persistence, CSV export, and sorting share one logical key.
 */
import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./DeviceStatus.css";
import { format } from "date-fns";
import { Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppIconButton from "../../Common/Button/IconButton";
import ConfiguredDataTable from "../../Common/DataTable/ConfiguredDataTable";
import { commonHeaders, deviceStatusTableConfig } from "./DeviceStatusConstants";
import { formatTableHeader } from "../../../utils/general";

const DisplayDeviceStatusData = ({
    deviceCategory,
    deviceStatusData = [],
    setDialog,
    setDialogContents,
    onVisibleColumnsChange,
    onVisibleColumnLabelsChange,
    onVisibleColumnDefsChange,
    pageIndex,
    pageSize,
    onFilteredRowCountChange,
}) => {
    const [headers, setHeaders] = useState({});

    // Payload uses `ts` for Unix ts; unify with `createdAt` for columns, persisted keys, export, and default sort.
    const normalizeKey = (key) => {
        if (key === "ts") return "createdAt";
        return key;
    };

    const getPayloadEntry = (row, key) => {
        if (key === "createdAt") {
            return row?.payload?.createdAt ?? row?.payload?.ts;
        }
        return row?.payload?.[key];
    };

    const getDisplayValue = (row, key) => {
        if (key === "serialNumber") return String(row?.serialNumber ?? "");
        if (key === "createdAt") {
            const payloadValue = getPayloadEntry(row, key)?.value;
            if (String(payloadValue || "").length === 0) return "";
            return format(new Date(Number(payloadValue) * 1000), "dd MMM yyyy, hh:mm a");
        }
        const payloadEntry = getPayloadEntry(row, key);
        if (!payloadEntry) return "-";
        if (Array.isArray(payloadEntry?.value)) return payloadEntry.value.join(", ");
        return payloadEntry?.value ?? "-";
    };

    const getSortValue = (row, key) => {
        if (key === "createdAt") {
            return Number(getPayloadEntry(row, key)?.value || 0);
        }
        return String(getDisplayValue(row, key) || "").toLowerCase();
    };

    useEffect(() => {
        const dataHeaders = {};
        deviceStatusData?.forEach(d => {
            const payload = d?.payload ?? {};
            Object.keys(payload).forEach(h => {
                const normalizedKey = normalizeKey(h);
                if (!dataHeaders?.hasOwnProperty(normalizedKey)) {
                    dataHeaders[normalizedKey] = {
                        header: formatTableHeader(payload[h]?.header !== "" ? payload[h]?.header : normalizedKey),
                        desc: payload[h]?.desc
                    };
                }
            });
        });
        delete dataHeaders?.mo;
        delete dataHeaders?.ty;

        const mappedCommonHeaders = Object.fromEntries(
            Object.entries(commonHeaders).map(([key, val]) => [normalizeKey(key), val])
        );

        setHeaders({
            ...mappedCommonHeaders,
            ...dataHeaders,
            actions: { header: "Actions", desc: "" }
        });
    }, [deviceStatusData]);

    const statusRows = Array.isArray(deviceStatusData)
        ? deviceStatusData.map((element, index) => ({
            rowId: `${element?.payload?.ts?.value || "status"}-${index}`,
            raw: element,
            payload: element?.payload || {},
            serialNumber: index + 1,
        }))
        : [];

    const tableConfig = useMemo(() => ({
        ...deviceStatusTableConfig,
        columns: {
            ...deviceStatusTableConfig.columns,
            actions: {
                ...deviceStatusTableConfig.columns.actions,
                headerCellSx: {
                    ...deviceStatusTableConfig.columns.actions?.headerCellSx,
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                },
            },
        },
    }), []);

    const normalizedHeaders = useMemo(() => (
        Object.fromEntries(
            Object.entries(headers).map(([key, val]) => [normalizeKey(key), { ...val, header: val?.header || formatTableHeader(key) }])
        )
    ), [headers]);

    return (
        <Fragment>
            {deviceCategory && deviceCategory !== "" && deviceStatusData && (
                <ConfiguredDataTable
                    config={tableConfig}
                    headers={normalizedHeaders}
                    rows={statusRows}
                    getCellDisplayValue={getDisplayValue}
                    getCellSortValue={getSortValue}
                    onVisibleColumnsChange={onVisibleColumnsChange}
                    onVisibleColumnLabelsChange={onVisibleColumnLabelsChange}
                    onVisibleColumnDefsChange={onVisibleColumnDefsChange}
                    onFilteredRowCountChange={onFilteredRowCountChange}
                    page={pageIndex}
                    pageSize={pageSize}
                    renderCell={({ column, row }) => {
                        if (column.key === "actions") {
                            return (
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <AppIconButton
                                        variant="primary"
                                        size="small"
                                        icon={<VisibilityIcon fontSize="small" />}
                                        tooltip="View status details"
                                        onClick={() => {
                                            setDialogContents({ title: "Status Details", data: row.raw });
                                            setDialog(true);
                                        }}
                                    />
                                </Box>
                            );
                        }
                        const displayValue = getDisplayValue(row, column.key);
                        return (
                            <Box className={String(displayValue || "").length > 200 ? "breakWord" : ""}>
                                {displayValue}
                            </Box>
                        );
                    }}
                    dataTableProps={{
                        rowKey: "rowId",
                        minWidth: "100%",
                        maxHeight: 400,
                        striped: false,
                        hoverEffect: false,
                        tableContainerSx: { boxShadow: "none", borderRadius: 0, width: "100%", minWidth: 0 },
                        sx: { mb: 2 },
                        msgForDisplay: "No Data found",
                    }}
                />
            )}
        </Fragment>
    );
};

export default DisplayDeviceStatusData